package uz.mint.frontend.components.sections

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import uz.mint.frontend.i18n.SiteStrings
import uz.mint.frontend.PageGutter
import uz.mint.frontend.toSitePalette
import uz.mint.frontend.alignItems
import uz.mint.frontend.alignSelf
import uz.mint.frontend.flexWrapCss
import uz.mint.frontend.gridTemplateColumnsCss
import uz.mint.frontend.hairlineCell
import uz.mint.frontend.justifyContent
import uz.mint.frontend.marginInlineAuto
import uz.mint.frontend.placeItems
import uz.mint.frontend.widthCss
import com.varabyte.kobweb.compose.ui.thenIf

@Composable
fun Stats(strings: SiteStrings) {
    val palette = ColorMode.current.toSitePalette()

    Div(
        Modifier
            .fillMaxWidth()
            .backgroundColor(palette.paper2)
            .borderBottom(2.px, LineStyle.Solid, palette.rule)
            .toAttrs()
    ) {
        // The dividers still belong to the grid rather than to any one cell — side by side the
        // gaps are vertical rules, stacked they become horizontal ones, and `auto-fit` decides
        // which without a media query. A plain border on a cell cannot do that: a left rule reads
        // as a divider only while the cells are in a row, and became a stray tick down the side of
        // each block once the grid reflowed to one column.
        //
        // What changed is where the colour comes from. It used to be the container's own
        // background showing through the gaps, which also filled the cells that do not exist —
        // three stats in two columns leave one empty, and in the light theme that empty half was a
        // block of near-black green. Now each cell draws its own outline and the container is
        // filled like a cell, so the lines exist only between real cells.
        //
        // `overflow: hidden` is what keeps that identical to before: this strip has no padding and
        // bleeds edge to edge, so without clipping the outer cells' outlines would add a frame the
        // design does not have.
        Div(
            Modifier
                .fillMaxWidth().maxWidth(1360.px).marginInlineAuto()
                .display(DisplayStyle.Grid)
                .backgroundColor(palette.paper2)
                .overflow(Overflow.Hidden)
                .gap(2.px)
                .gridTemplateColumnsCss("repeat(auto-fit,minmax(min(100%,240px),1fr))")
                .toAttrs()
        ) {
            Stat(strings.stat1Value, strings.stat1, palette.ink2, palette.paper2, palette.rule)
            Stat(strings.stat2Value, strings.stat2, palette.ink2, palette.paper2, palette.rule)
            Stat(strings.stat3Value, strings.stat3, palette.ink2, palette.paper2, palette.rule)
        }
    }
}

@Composable
private fun Stat(value: String, label: String, ink2: Color, background: Color, rule: Color) {
    Row(
        Modifier
            .fillMaxWidth()
            .backgroundColor(background)
            .hairlineCell(rule)
            .padding(topBottom = 26.px, leftRight = PageGutter)
            .gap(14.px)
            // Numerals and their labels sit on a shared baseline in the design.
            .alignItems("baseline"),
    ) {
        SpanText(value, Modifier.fontSize(34.px).fontWeight(FontWeight.ExtraBold).letterSpacing((-0.04).em))
        SpanText(label, Modifier.fontSize(14.px).fontWeight(FontWeight.SemiBold).color(ink2))
    }
}
